/**
 * Media player module for audio and video playback
 */

import YouTubePlayer from 'youtube-player';
import { getDifferenceInSeconds, formatSecondsAsTime, isNowBetweenDates, isTouchDevice } from './utils.js';

// Player state
export const player = {
  currentEp: false,
  audio: false,
  video: false,
  playing: false,
  elm: false,
  loading: false,
  channel: false,
  startTime: null,
};

export function initPlayerState(startTime) {
  player.startTime = startTime;
}

export function bindPlayers(schedule, loadPageCallback) {
  const playButtons = document.querySelectorAll('.play-trigger');

  for (let a = 0; a < playButtons.length; a++) {
    const button = playButtons[a];

    if (!button.classList.contains('bound')) {
      button.classList.add('bound');
      const sidebar = button.closest('.sidebar');
      button.addEventListener('click', (e) => togglePlay(e, schedule, loadPageCallback));

      // Keyboard accessibility for play button
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          togglePlay(e, schedule, loadPageCallback);
          updateAriaPressed(button, player.playing);
        }
      });

      // Auto-advance to next episode only if already playing
      if (player.queElm === sidebar && player.playing) {
        player.elm.classList.remove('active');
        player.playing = false;
        togglePlay(sidebar, schedule, loadPageCallback);
      }

      // Auto-play if this is a live episode
      if (sidebar.dataset.autoplay === 'true') {
        sidebar.dataset.autoplay = 'false'; // Prevent re-triggering
        togglePlay({ target: button, preventDefault: () => {} }, schedule, loadPageCallback);
      }
    }
  }

  // Bind keyboard controls for media
  bindKeyboardControls(schedule, loadPageCallback);
}

/**
 * Update ARIA pressed state for toggle buttons
 */
function updateAriaPressed(button, isPressed) {
  button.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
  button.setAttribute('aria-label', isPressed ? 'Pause' : 'Play');
}

/**
 * Bind global keyboard shortcuts for media control
 */
function bindKeyboardControls(schedule, loadPageCallback) {
  if (window.keyboardControlsBound) return;
  window.keyboardControlsBound = true;

  document.addEventListener('keydown', (e) => {
    // Only handle if not typing in an input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    switch (e.key) {
      case ' ':
        // Space to play/pause
        e.preventDefault();
        const playButton = document.querySelector('.play-trigger');
        if (playButton) {
          togglePlay({ target: playButton, preventDefault: () => {} }, schedule, loadPageCallback);
        }
        break;
      case 'm':
      case 'M':
        // M to mute/unmute
        const soundButton = document.querySelector('.sound-trigger');
        if (soundButton) soundButton.click();
        break;
      case 'f':
      case 'F':
        // F for fullscreen
        const fsButton = document.querySelector('.fullscreen-trigger');
        if (fsButton) fsButton.click();
        break;
    }
  });
}

function togglePlay(e, schedule, _loadPageCallback) {
  let sidebar = false;

  if (e.target) {
    e.preventDefault();
    sidebar = e.target.closest('.sidebar');
  } else {
    sidebar = e;
  }

  if (player.audio && player.playing) {
    player.audio.pause();
    document.querySelectorAll('.sidebar').forEach((el) => {
      el.classList.remove('playing');
    });
  }

  if (player.video && player.playing) {
    player.video.pauseVideo();
    document.querySelectorAll('.sidebar').forEach((el) => {
      el.classList.remove('playing');
    });
  }

  let channelData = false;
  let episodeData = false;
  let scheduleData = false;

  if (sidebar) {
    const channel = sidebar.dataset.channel;
    const episode = sidebar.dataset.episode;

    if (player.playing && player.currentEp && player.currentEp.episode[0].slug === episode) {
      player.playing = false;
      return false;
    }

    for (let a = 0; a < schedule.length; a++) {
      const data = schedule[a];
      if (data.slug === channel) {
        channelData = data;
        scheduleData = data.schedule;
      }
    }

    if (channelData && scheduleData) {
      for (let a = 0; a < scheduleData.length; a++) {
        const schblock = scheduleData[a];
        const ep = scheduleData[a]['episode'][0];

        if (ep.slug === episode && isNowBetweenDates(schblock.startTime, schblock.endTime)) {
          episodeData = schblock;
        }
      }
    }

    if (episodeData) {
      player.channel = channelData;
      if (player.elm !== sidebar) {
        if (player.elm) {
          const old = player.elm;
          sidebar.classList.remove('second-player');
          old.classList.remove('.active');
          setTimeout(function () {
            old.parentNode.removeChild(old);
          }, 200);
        }
      }

      player.currentEp = episodeData;
      player.elm = sidebar;
      playContent();
    }
  }
}

/**
 * When a video/audio finishes, advance to the next episode in the channel schedule.
 * Reuses the existing player instance to preserve autoplay permission from the
 * original user gesture — no new Audio/YT player objects needed.
 */
function advanceToNextEpisode() {
  if (!player.channel || !player.channel.schedule || !player.currentEp) return;

  const schedule = player.channel.schedule;
  const currentStart = player.currentEp.startTime;
  let currentIndex = -1;

  // Match by startTime (not slug) to handle repeated episodes
  for (let i = 0; i < schedule.length; i++) {
    if (schedule[i].startTime === currentStart) {
      currentIndex = i;
      break;
    }
  }

  if (currentIndex < 0 || currentIndex >= schedule.length - 1) return;

  const nextBlock = schedule[currentIndex + 1];
  const nextEp = nextBlock.episode[0];

  // Update player state
  player.currentEp = nextBlock;

  // Update sidebar UI in place (preserves fullscreen / large-mode)
  if (player.elm) {
    player.elm.dataset.episode = nextEp.slug;

    // Title
    const titleEl = player.elm.querySelector('.title h1');
    if (titleEl) titleEl.textContent = nextEp.title;

    // Description
    const descEl = player.elm.querySelector('.description');
    if (descEl) {
      descEl.innerHTML = nextEp.description || '';
    }

    // Cover image — swap to YouTube thumb if video, keep existing show cover otherwise
    if (nextEp.youtubeId) {
      const img = player.elm.querySelector('.media > img');
      if (img) {
        img.src = `https://img.youtube.com/vi/${nextEp.youtubeId}/hqdefault.jpg`;
        img.alt = nextEp.title;
      }
    }

    // Upcoming list — rebuild from remaining schedule
    updateUpcomingList(schedule, currentIndex + 1);

    // Browser URL
    const channelSlug = player.elm.dataset.channel;
    if (channelSlug) {
      const url = `/channel/${channelSlug}`;
      history.replaceState(null, '', url);
    }
  }

  // Reuse existing player instance to keep autoplay context
  if (player.video && nextEp.youtubeId) {
    // Load next video into existing YouTube player — no new iframe needed
    player.video.loadVideoById({
      videoId: nextEp.youtubeId,
      startSeconds: 0,
    });
    if (player.elm) player.elm.classList.add('playing');
    player.playing = true;
  } else if (player.audio && nextEp.mp3Url) {
    // Reuse existing Audio element — change src and play
    const url = nextEp.mp3Url.replace('http://', 'https://');
    player.audio.src = url;
    player.audio.currentTime = 0;
    player.audio.play().catch(() => {
      console.warn('Audio play failed on advance — trying next');
      advanceToNextEpisode();
    });
    if (player.elm) player.elm.classList.add('playing');
    player.playing = true;
  } else {
    // Media type changed (audio→video or vice versa), fall back to full playContent
    playContent();
  }
}

/**
 * Rebuild the upcoming episode list in the sidebar from schedule data.
 * currentIndex is the index of the now-playing block.
 */
function updateUpcomingList(schedule, currentIndex) {
  if (!player.elm) return;
  const upcomingEl = player.elm.querySelector('.upcoming');
  if (!upcomingEl) return;

  // Clear existing items (keep the heading)
  upcomingEl.querySelectorAll('.upcoming-item').forEach(el => el.remove());

  const channelSlug = player.elm.dataset.channel || '';
  const now = new Date();

  // Collect up to 6 future blocks after the current one
  let count = 0;
  for (let i = currentIndex + 1; i < schedule.length && count < 6; i++) {
    const block = schedule[i];
    const ep = block.episode[0];
    const startTime = new Date(block.startTime);
    if (startTime <= now) continue;

    const link = document.createElement('a');
    link.className = 'upcoming-item episode';
    link.dataset.episode = ep.slug;
    const epUrl = ep.url || `/episode/${ep.slug}`;
    link.href = `${epUrl}?channel=${channelSlug}&startTime=${block.startTime}`;

    const titleSpan = document.createElement('span');
    titleSpan.className = 'upcoming-title';
    titleSpan.textContent = ep.title;

    const timeSpan = document.createElement('span');
    timeSpan.className = 'upcoming-time';
    timeSpan.textContent = startTime.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });

    link.appendChild(titleSpan);
    link.appendChild(timeSpan);

    if (ep.runtime) {
      const durSpan = document.createElement('span');
      durSpan.className = 'upcoming-duration';
      durSpan.textContent = Math.round(ep.runtime / 60) + 'min';
      link.appendChild(durSpan);
    }

    upcomingEl.appendChild(link);
    count++;
  }

  // Hide section if nothing upcoming
  upcomingEl.style.display = count > 0 ? '' : 'none';
}

export function playContent() {
  const episode = player.currentEp.episode[0];

  if (player.currentEp) {
    if (episode.mp3Url) {
      playAudio();
    } else {
      playVideo();
    }
  }
}

function playAudio() {
  const episode = player.currentEp.episode[0];
  const seconds = getDifferenceInSeconds(player.currentEp.startTime) * -1;

  if (episode.mp3Url) {
    const url = episode.mp3Url.replace('http://', 'https://');
    // Release any previous Audio buffer before allocating a new one — Safari
    // in particular holds onto the decoded buffer indefinitely otherwise.
    if (player.audio) {
      player.audio.pause();
      player.audio.src = '';
      try { player.audio.load(); } catch (e) { /* noop */ }
    }
    player.audio = new Audio(url);
    player.audio.currentTime = seconds;
    player.audio.play().catch(() => {
      console.warn('Audio play rejected — advancing to next episode');
      advanceToNextEpisode();
    });

    document.querySelectorAll('.sidebar').forEach((el) => {
      el.classList.remove('playing');
    });
    if (player.elm) player.elm.classList.add('playing');

    player.audio.addEventListener('timeupdate', () => {
      updateTrackTime();
    });

    player.audio.addEventListener('ended', () => {
      advanceToNextEpisode();
    });

    player.audio.addEventListener('error', () => {
      console.warn('Audio load error — advancing to next episode');
      advanceToNextEpisode();
    });

    player.playing = true;
    player.loading = false;
    player.queElm = false;
  }
}

function playVideo() {
  const episode = player.currentEp.episode[0];
  const seconds = getDifferenceInSeconds(player.currentEp.startTime) * -1;
  const controls = player.elm.querySelector('.controls');
  let vidWrap = player.elm.querySelector('.vid-wrap');
  const media = player.elm.querySelector('.media');

  if (vidWrap.tagName === 'IFRAME') {
    vidWrap.parentNode.removeChild(vidWrap);
    vidWrap = document.createElement('div');
    vidWrap.classList.add('vid-wrap');
    media.appendChild(vidWrap);
  }

  if (episode.youtubeId) {
    const isMobile = isTouchDevice();
    const ytplayer = YouTubePlayer(vidWrap, {
      height: '360',
      width: '640',
      videoId: `${episode.youtubeId}`,
      playerVars: {
        autoplay: 1,
        controls: 0,
        showinfo: 0,
        start: seconds,
        modestbranding: 1,
        rel: 0,
        fs: 0,
        disablekb: 1,
        playsinline: 1,         // iOS Safari requires this for inline playback
        mute: isMobile ? 1 : 0, // mobile autoplay is only allowed when muted
      },
    });

    media.classList.add('vid');
    document.querySelectorAll('.sidebar').forEach((el) => {
      el.classList.remove('playing');
    });
    if (player.elm) player.elm.classList.add('playing');
    player.playing = true;
    player.video = ytplayer;

    // Mobile starts muted to satisfy autoplay policy. Show a tap-to-unmute
    // affordance so the user can opt back in with a fresh gesture.
    if (isMobile) {
      showUnmutePrompt(media, ytplayer);
      // Fallback: if PLAYING never fires within ~2.5s (autoplay blocked entirely),
      // overlay a centred tap-to-play button that calls playVideo() in a fresh gesture.
      let started = false;
      ytplayer.on('stateChange', (event) => { if (event.data === 1) started = true; });
      setTimeout(() => {
        if (!started) showTapToPlay(media, ytplayer);
      }, 2500);
    }

    player.loading = false;
    player.queElm = false;

    let timeUpdater = null;

    const startUpdatingTime = () => {
      timeUpdater = setInterval(() => {
        ytplayer.getCurrentTime().then((value) => {
          const currTimeDiv = player.elm.querySelector('.current-time');
          const currTime = Math.floor(value).toString();
          currTimeDiv.innerHTML = formatSecondsAsTime(currTime);
        });
      }, 100);
    };

    const stopUpdatingTime = () => {
      clearInterval(timeUpdater);
    };

    ytplayer.on('stateChange', (event) => {
      if (event.data === 0) {
        if (player.elm) player.elm.classList.remove('playing');
        stopUpdatingTime();
        advanceToNextEpisode();
      } else if (event.data === 2) {
        if (player.elm) player.elm.classList.remove('playing');
        stopUpdatingTime();
      }

      if (event.data === 1) {
        if (player.elm) player.elm.classList.add('playing');
        player.playing = true;
        startUpdatingTime();
      }

      ytplayer.getDuration().then((value) => {
        const durationDiv = player.elm.querySelector('.duration');
        const duration = Math.floor(value).toString();

        if (isNaN(duration)) {
          durationDiv.innerHTML = '00:00';
        } else {
          durationDiv.innerHTML = formatSecondsAsTime(duration);
        }
      });
    });

    // YouTube error (unavailable, not embeddable, etc.) — skip to next
    ytplayer.on('error', (event) => {
      console.warn('YouTube player error:', event.data, '— advancing to next episode');
      stopUpdatingTime();
      advanceToNextEpisode();
    });
  }
}

function updateTrackTime() {
  const currTimeDiv = player.elm.querySelector('.current-time');
  const durationDiv = player.elm.querySelector('.duration');

  const currTime = Math.floor(player.audio.currentTime).toString();
  const duration = Math.floor(player.audio.duration).toString();

  currTimeDiv.innerHTML = formatSecondsAsTime(currTime);

  if (isNaN(duration)) {
    durationDiv.innerHTML = '00:00';
  } else {
    durationDiv.innerHTML = formatSecondsAsTime(duration);
  }
}

function showUnmutePrompt(mediaEl, ytplayer) {
  if (!mediaEl) return;
  // Reuse if already present
  if (mediaEl.querySelector('.unmute-prompt')) return;

  // Full-area transparent button — tapping anywhere on the video unmutes.
  // Avoids iOS Safari's tendency to give the YouTube iframe priority for
  // touch events when the tap target is a small corner badge.
  const prompt = document.createElement('button');
  prompt.type = 'button';
  prompt.className = 'unmute-prompt';
  prompt.setAttribute('aria-label', 'Tap to unmute');
  prompt.innerHTML = '<span class="unmute-prompt__badge"><span aria-hidden="true">🔇</span> Tap to unmute</span>';

  const dismiss = (e) => {
    if (e && e.cancelable) e.preventDefault();
    try { ytplayer.unMute(); } catch (err) { /* noop */ }
    prompt.remove();
  };
  // Use pointerup where available (handles mouse + touch + pen consistently);
  // fall back to click for older browsers.
  if ('onpointerup' in window) {
    prompt.addEventListener('pointerup', dismiss);
  } else {
    prompt.addEventListener('click', dismiss);
    prompt.addEventListener('touchend', dismiss);
  }

  mediaEl.appendChild(prompt);
}

// Last-ditch fallback for mobile when even muted autoplay is blocked.
// Renders a centred play button; tapping it issues playVideo() inside a real
// user gesture, which is allowed.
function showTapToPlay(mediaEl, ytplayer) {
  if (!mediaEl) return;
  if (mediaEl.querySelector('.tap-to-play')) return;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'tap-to-play';
  btn.setAttribute('aria-label', 'Tap to play');
  btn.innerHTML = '<span class="tap-to-play__icon" aria-hidden="true">▶</span><span class="tap-to-play__label">Tap to play</span>';

  const start = (e) => {
    e.preventDefault();
    try { ytplayer.playVideo(); } catch (err) { /* noop */ }
    btn.remove();
  };
  btn.addEventListener('click', start);
  btn.addEventListener('touchend', start);

  mediaEl.appendChild(btn);
}

export function stopPlayer() {
  if (player.audio) {
    player.audio.pause();
    player.audio.src = '';
    try { player.audio.load(); } catch (e) { /* noop */ }
    player.audio = false;
  }

  if (player.video) {
    try { player.video.pauseVideo(); } catch (e) { /* noop */ }
    try { player.video.destroy(); } catch (e) { /* noop */ }
    player.video = false;
  }
}

export function resetPlayer() {
  stopPlayer();
  player.elm = false;
  player.playing = false;
  player.channel = false;
  player.currentEp = false;
}
